import { useMutation } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { requestAdminPasswordReset } from '../api/auth';
import { ApiError } from '../api/client';
import { Button, Input } from '../components/ui';
import styles from './LoginPage.module.scss';

function readPrefilledEmail(state: unknown): string {
  const email = (state as { email?: unknown } | null)?.email;
  return typeof email === 'string' ? email : '';
}

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState(() => readPrefilledEmail(location.state));

  const mutation = useMutation({
    mutationFn: () => requestAdminPasswordReset({ email: email.trim() }),
    onSuccess: (response) => {
      // History state, not the query string, so the token stays out of the
      // address bar, browser history and Referer headers.
      navigate('/admin/reset-password', {
        state: { challengeToken: response.challenge_token, maskedEmail: response.masked_email },
      });
    },
  });

  const errorMessage =
    mutation.error instanceof ApiError ? mutation.error.message : t('auth.forgotPasswordError');

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!mutation.isPending) mutation.mutate();
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.brand}>
          <span className={styles.brandLabel}>White&amp;Flex</span>
          <h1 className={styles.title}>{t('auth.forgotPasswordTitle')}</h1>
          <p className={styles.subtitle}>{t('auth.forgotPasswordHint')}</p>
        </div>

        {mutation.isError ? (
          <div className={styles.alert} role="alert">
            {errorMessage}
          </div>
        ) : null}

        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.fields}>
            <Input
              id="email"
              type="email"
              label={t('auth.email')}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="email"
              required
              disabled={mutation.isPending}
            />
          </div>

          <Button type="submit" variant="primary" size="lg" fullWidth disabled={mutation.isPending}>
            {mutation.isPending ? t('auth.sendingResetCode') : t('auth.forgotPasswordSubmit')}
          </Button>

          <Link to="/login" className={styles.textLink}>
            {t('auth.backToSignIn')}
          </Link>
        </form>
      </div>
    </div>
  );
}
